import { createSlice } from "@reduxjs/toolkit";
import Cookies from "js-cookie";

const TOKEN_COOKIE = "tc_token";
// A global admin has no tenant roles of their own, so their header tenant selection would be lost on every
// page reload. Keep it in sessionStorage (per tab, gone when the tab closes).
const ADMIN_TENANT_KEY = "tc-global-admin-tenant";

function readSavedAdminTenant() {
  try { return sessionStorage.getItem(ADMIN_TENANT_KEY) || null; } catch { return null; }
}
function saveAdminTenant(tenantId) {
  try {
    if (tenantId) sessionStorage.setItem(ADMIN_TENANT_KEY, tenantId);
    else sessionStorage.removeItem(ADMIN_TENANT_KEY);
  } catch { /* storage unavailable - selection just won't survive a reload */ }
}

// The JWT token is ONLY stored in the cookie — never in Redux state.
// Redux holds user profile fields, tenantRoles map, activeTenantId,
// and transient UI state (loading, error).
function getInitialState() {
  const hasToken = !!Cookies.get(TOKEN_COOKIE);
  return {
    userId: null,
    tenantRoles: {}, // e.g. { "tenantA": ["STUDENT","COORDINATOR"], "tenantB": ["INSTRUCTOR"] }
    activeTenantId: null, // currently selected tenant
    agencyId: null, // agency ID (for agency admins)
    agencyName: null, // agency name (for agency admins)
    email: null,
    username: null,
    fullName: null,
    roles: [], // global roles (e.g. ADMIN)
    isAuthenticated: hasToken,
    loading: false,
    error: null,
    impersonating: null, // {name, roleLabel, tenantName} when impersonating
  };
}

function pickFirstTenant(tenantRoles) {
  const keys = Object.keys(tenantRoles || {});
  return keys.length > 0 ? keys[0] : null;
}

const authSlice = createSlice({
  name: "auth",
  initialState: getInitialState(),
  reducers: {
    loginStart(state) {
      state.loading = true;
      state.error = null;
    },
    loginSuccess(state, action) {
      const {
        accessToken,
        userId,
        tenantRoles,
        agencyId,
        agencyName,
        email,
        username,
        fullName,
        roles,
        expiresIn,
      } = action.payload;
      state.userId = userId;
      state.tenantRoles = tenantRoles || {};
      state.activeTenantId = pickFirstTenant(tenantRoles);
      state.agencyId = agencyId || null;
      state.agencyName = agencyName || null;
      state.email = email;
      state.username = username;
      state.fullName = fullName || null;
      state.roles = Array.isArray(roles) ? roles : Array.from(roles || []);
      state.isAuthenticated = true;
      state.loading = false;
      state.error = null;

      // Store JWT in a cookie. Secure flag is only set when served over
      // HTTPS; SameSite=Strict prevents CSRF.
      const expiresDays = expiresIn ? expiresIn / (1000 * 60 * 60 * 24) : 1;
      Cookies.set(TOKEN_COOKIE, accessToken, {
        expires: expiresDays,
        sameSite: "Strict",
        secure: window.location.protocol === "https:",
      });
    },
    loginFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
    },
    restoreUser(state, action) {
      const {
        userId,
        tenantRoles,
        agencyId,
        agencyName,
        email,
        username,
        fullName,
        roles,
      } = action.payload;
      state.userId = userId;
      state.tenantRoles = tenantRoles || {};
      const isGlobalAdmin = Array.isArray(roles) ? roles.includes("GLOBAL_ADMIN") : !!roles?.has?.("GLOBAL_ADMIN");
      if (isGlobalAdmin) {
        // Global admins pick their tenant from the header; bring back the last selection.
        state.activeTenantId = state.activeTenantId || readSavedAdminTenant();
      } else if (!state.activeTenantId || !tenantRoles[state.activeTenantId]) {
        // Keep activeTenantId if still valid, otherwise pick first
        state.activeTenantId = pickFirstTenant(tenantRoles);
      }
      state.agencyId = agencyId || null;
      state.agencyName = agencyName || null;
      state.email = email;
      state.username = username;
      state.fullName = fullName || null;
      state.roles = Array.isArray(roles) ? roles : Array.from(roles || []);
      state.isAuthenticated = true;
      state.loading = false;
    },
    setActiveTenant(state, action) {
      const tenantId = action.payload;
      if (tenantId && state.tenantRoles[tenantId]) {
        state.activeTenantId = tenantId;
      }
    },
    forceSetActiveTenant(state, action) {
      state.activeTenantId = action.payload || null;
      saveAdminTenant(state.activeTenantId);
    },
    logout(state) {
      saveAdminTenant(null);
      state.userId = null;
      state.tenantRoles = {};
      state.activeTenantId = null;
      state.agencyId = null;
      state.agencyName = null;
      state.email = null;
      state.username = null;
      state.fullName = null;
      state.roles = [];
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      state.impersonating = null;
      Cookies.remove(TOKEN_COOKIE);
    },
    startImpersonationSession(state, action) {
      state.impersonating = action.payload;
    },
    endImpersonationSession(state) {
      state.impersonating = null;
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  restoreUser,
  setActiveTenant,
  forceSetActiveTenant,
  logout,
  startImpersonationSession,
  endImpersonationSession,
} = authSlice.actions;
export const TOKEN_COOKIE_NAME = TOKEN_COOKIE;

// Selectors
export const selectActiveTenantId = (state) => state.auth.activeTenantId;
export const selectTenantRoles = (state) => state.auth.tenantRoles;

export default authSlice.reducer;
